import React, { useState, useCallback } from 'react';
import { HilEngine } from '@flow-studio/flow-observer-core';
import type { HilEvent } from '@flow-studio/flow-observer-core';
import { HilObservePanel } from './HilObservePanel.js';

// ─── Types ───────────────────────────────────────────────────────────────────

type EventType = 'APPROVAL' | 'FORM' | 'CHAT';

interface ScenarioConfig {
  eventType: EventType;
  title: string;
  description: string;
  strategy: string;
  inboxItemId: number;
  formId: number | undefined;
  actorId: string;
  userId: number | undefined;
  requireReason: boolean;
  expirySeconds: number | undefined;
  channel: string;
}

// ─── Presets ─────────────────────────────────────────────────────────────────

const PRESETS: Array<{ label: string; badge: string; config: Partial<ScenarioConfig> }> = [
  {
    label: 'Approval — Admin',
    badge: 'APPROVAL',
    config: {
      eventType: 'APPROVAL',
      title: 'Budget Approval Request',
      description: 'Finance team is requesting approval for Q3 budget allocation of $50,000. Please review the attached breakdown and confirm.',
      strategy: 'FirstResponse',
      actorId: 'admin',
      requireReason: false,
    },
  },
  {
    label: 'Approval — Require Reason',
    badge: 'APPROVAL',
    config: {
      eventType: 'APPROVAL',
      title: 'Policy Exception Request',
      description: 'An employee is requesting an exception to the remote-work policy. A reason is mandatory for this approval.',
      strategy: 'Unanimous',
      actorId: 'hr-admin',
      requireReason: true,
    },
  },
  {
    label: 'Approval — Majority Vote',
    badge: 'APPROVAL',
    config: {
      eventType: 'APPROVAL',
      title: 'New Vendor Onboarding',
      description: 'Procurement team requests approval for a new vendor contract. Majority vote required.',
      strategy: 'MajorityVote',
      actorId: 'admin',
      requireReason: false,
    },
  },
  {
    label: 'Approval — Expired',
    badge: 'APPROVAL',
    config: {
      eventType: 'APPROVAL',
      title: 'Overdue Approval',
      description: 'This approval request has already passed its deadline. The session is now expired.',
      strategy: 'FirstResponse',
      actorId: 'admin',
      requireReason: false,
      expirySeconds: -30,
    },
  },
  {
    label: 'Approval — 30 s Timer',
    badge: 'APPROVAL',
    config: {
      eventType: 'APPROVAL',
      title: 'Urgent Compliance Sign-off',
      description: 'Compliance requires immediate sign-off. You have 30 seconds.',
      strategy: 'FirstResponse',
      actorId: 'compliance-admin',
      requireReason: false,
      expirySeconds: 30,
    },
  },
  {
    label: 'Approval + Form (ID 1)',
    badge: 'APPROVAL',
    config: {
      eventType: 'APPROVAL',
      title: 'Contract Review & Approval',
      description: 'Please review the contract details below, complete the required form, then cast your vote.',
      strategy: 'Unanimous',
      actorId: 'admin',
      requireReason: false,
      formId: 1,
    },
  },
  {
    label: 'Form — Text Fallback',
    badge: 'FORM',
    config: {
      eventType: 'FORM',
      title: 'Onboarding Survey',
      description: 'Please complete the new-employee onboarding survey before your first day.',
      actorId: 'admin',
      formId: undefined,
    },
  },
  {
    label: 'Form — With Form ID',
    badge: 'FORM',
    config: {
      eventType: 'FORM',
      title: 'Expense Claim Form',
      description: 'Submit your expense claim via the form below.',
      actorId: 'admin',
      formId: 1,
    },
  },
  {
    label: 'Chat Task',
    badge: 'CHAT',
    config: {
      eventType: 'CHAT',
      title: 'Clarification Required',
      description: 'The AI agent needs additional information before it can proceed. Please reply in the chat below.',
      actorId: 'admin',
    },
  },
];

const DEFAULT_CONFIG: ScenarioConfig = {
  eventType: 'APPROVAL',
  title: 'Expense Report Approval',
  description: 'Please review and approve the Q2 expense report for the Marketing team.',
  strategy: 'Unanimous',
  inboxItemId: 0,
  formId: undefined,
  actorId: 'admin',
  userId: undefined,
  requireReason: false,
  expirySeconds: undefined,
  channel: 'workdesk',
};

const EVENT_TYPE_COLORS: Record<string, string> = {
  APPROVAL: '#3b82f6',
  FORM: '#8b5cf6',
  CHAT: '#10b981',
};

let _counter = 1;

// ─── Component ───────────────────────────────────────────────────────────────

export const HilTestPage: React.FC = () => {
  const [cfg, setCfg] = useState<ScenarioConfig>({ ...DEFAULT_CONFIG });
  const [log, setLog] = useState<string[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  const pushLog = (msg: string) =>
    setLog(prev => [`[${new Date().toLocaleTimeString()}] ${msg}`, ...prev.slice(0, 14)]);

  // ── Inject event ──────────────────────────────────────────────────────────
  const inject = useCallback(() => {
    const id = `test-hil-${_counter++}`;
    const expiresAt = cfg.expirySeconds != null
      ? new Date(Date.now() + cfg.expirySeconds * 1000).toISOString()
      : undefined;

    const event: HilEvent = {
      id,
      executionResId: 'test-exec-res-id',
      nodeKey: 'test-node-key',
      nodeLabel: cfg.title || 'Test HIL Node',
      eventType: cfg.eventType,
      engageSessionId: `test-session-${_counter}`,
      inboxItemId: cfg.inboxItemId,
      formId: cfg.formId,
      channel: cfg.channel,
      expiresAt,
      payload: {
        title: cfg.title,
        description: cfg.description,
        strategy: cfg.strategy,
        requireReason: cfg.requireReason,
      },
      assignedActors: [
        {
          actorId: cfg.actorId,
          actorType: 'user',
          channel: cfg.channel,
          userId: cfg.userId ?? 0,
          inboxItemId: cfg.inboxItemId,
        },
      ],
    };

    HilEngine.pushEvent(event);
    setActiveId(id);
    pushLog(`Injected ${cfg.eventType} "${cfg.title}" (id=${id}, inboxItem=${cfg.inboxItemId})`);
  }, [cfg]);

  // ── Clear ─────────────────────────────────────────────────────────────────
  const clear = useCallback(() => {
    if (activeId) {
      HilEngine.resolveEvent(activeId);
      pushLog(`Resolved ${activeId}`);
      setActiveId(null);
    }
  }, [activeId]);

  // ── Load preset ───────────────────────────────────────────────────────────
  const loadPreset = (patch: Partial<ScenarioConfig>) => {
    setCfg({ ...DEFAULT_CONFIG, ...patch });
  };

  // ── Field helpers ─────────────────────────────────────────────────────────
  const label = (text: string) => (
    <span style={{ fontSize: '11px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase' as const, letterSpacing: '0.04em', display: 'block', marginBottom: '4px' }}>
      {text}
    </span>
  );

  const textInput = (value: string | number | undefined, onChange: (v: string) => void, placeholder?: string) => (
    <input
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={INPUT_STYLE}
    />
  );

  return (
    <div style={{ display: 'flex', height: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif', background: '#0f172a', overflow: 'hidden' }}>

      {/* ── Left panel: controls ─────────────────────────────────────────── */}
      <div style={{ width: '300px', flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: '1px solid #374151', background: '#1f2937', overflowY: 'auto' }}>

        {/* Header */}
        <div style={{ padding: '14px 16px', borderBottom: '1px solid #374151', background: '#111827', flexShrink: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: '#f9fafb' }}>HIL Event Test Console</div>
          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>Inject mock HIL events into the observe panel</div>
        </div>

        {/* Presets */}
        <div style={{ padding: '12px 14px', borderBottom: '1px solid #374151' }}>
          {label('Quick presets')}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {PRESETS.map(p => (
              <button
                key={p.label}
                onClick={() => loadPreset(p.config)}
                style={{
                  padding: '7px 10px',
                  borderRadius: '5px',
                  border: '1px solid #374151',
                  background: '#111827',
                  fontSize: '12px',
                  textAlign: 'left' as const,
                  cursor: 'pointer',
                  color: '#d1d5db',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '7px',
                }}
              >
                <span style={{
                  padding: '1px 6px',
                  borderRadius: '9999px',
                  background: EVENT_TYPE_COLORS[p.badge] ?? '#6b7280',
                  color: '#fff',
                  fontSize: '10px',
                  fontWeight: 700,
                  flexShrink: 0,
                }}>
                  {p.badge}
                </span>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Config form */}
        <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 }}>
          {label('Event config')}

          <div>
            {label('Event type')}
            <select
              value={cfg.eventType}
              onChange={e => setCfg(c => ({ ...c, eventType: e.target.value as EventType }))}
              style={INPUT_STYLE}
            >
              <option value="APPROVAL">APPROVAL</option>
              <option value="FORM">FORM</option>
              <option value="CHAT">CHAT</option>
            </select>
          </div>

          <div>
            {label('Title')}
            {textInput(cfg.title, v => setCfg(c => ({ ...c, title: v })), 'Request title')}
          </div>

          <div>
            {label('Description')}
            <textarea
              value={cfg.description}
              onChange={e => setCfg(c => ({ ...c, description: e.target.value }))}
              rows={3}
              placeholder="Request description…"
              style={{ ...INPUT_STYLE, resize: 'vertical' as const, minHeight: '60px' }}
            />
          </div>

          {cfg.eventType === 'APPROVAL' && (
            <div>
              {label('Strategy')}
              <select
                value={cfg.strategy}
                onChange={e => setCfg(c => ({ ...c, strategy: e.target.value }))}
                style={INPUT_STYLE}
              >
                <option value="FirstResponse">FirstResponse — any one approves</option>
                <option value="Unanimous">Unanimous — all must approve</option>
                <option value="MajorityVote">MajorityVote</option>
                <option value="SimpleThreshold">SimpleThreshold (N of M)</option>
              </select>
            </div>
          )}

          <div>
            {label('Actor ID / Role')}
            {textInput(cfg.actorId, v => setCfg(c => ({ ...c, actorId: v })), 'admin, reviewer-1, …')}
          </div>

          <div>
            {label('User ID (optional)')}
            {textInput(
              cfg.userId,
              v => setCfg(c => ({ ...c, userId: v ? parseInt(v) : undefined })),
              'numeric — leave blank if not needed'
            )}
          </div>

          <div>
            {label('Inbox Item ID')}
            {textInput(
              cfg.inboxItemId,
              v => setCfg(c => ({ ...c, inboxItemId: parseInt(v) || 0 })),
              '0 = mock only; real ID = hits API'
            )}
            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '3px' }}>
              Set to a real inbox item ID to test the full Approve / Reject API call.
            </div>
          </div>

          {cfg.eventType === 'FORM' && (
            <div>
              {label('Form ID (optional)')}
              {textInput(
                cfg.formId,
                v => setCfg(c => ({ ...c, formId: v ? parseInt(v) : undefined })),
                'blank = text fallback; real ID = loads Atlas form'
              )}
            </div>
          )}

          <div>
            {label('Expiry (seconds from now)')}
            {textInput(
              cfg.expirySeconds,
              v => setCfg(c => ({ ...c, expirySeconds: v !== '' ? parseInt(v) : undefined })),
              'blank = none; 30 = 30 s; -10 = already expired'
            )}
          </div>

          {cfg.eventType === 'APPROVAL' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '13px', color: '#d1d5db', cursor: 'pointer', userSelect: 'none' as const }}>
              <input
                type="checkbox"
                checked={cfg.requireReason}
                onChange={e => setCfg(c => ({ ...c, requireReason: e.target.checked }))}
              />
              Require reason field
            </label>
          )}
        </div>

        {/* Action buttons */}
        <div style={{ padding: '12px 14px', borderTop: '1px solid #374151', display: 'flex', gap: '8px', flexShrink: 0 }}>
          <button
            onClick={inject}
            style={{
              flex: 1,
              padding: '9px',
              borderRadius: '6px',
              border: 'none',
              background: '#3b82f6',
              color: '#fff',
              fontWeight: 700,
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            Inject Event
          </button>
          <button
            onClick={clear}
            disabled={!activeId}
            title={activeId ? `Resolve ${activeId}` : 'No active event'}
            style={{
              flex: 1,
              padding: '9px',
              borderRadius: '6px',
              border: '1px solid #4b5563',
              background: '#374151',
              color: '#d1d5db',
              fontWeight: 600,
              fontSize: '13px',
              cursor: activeId ? 'pointer' : 'not-allowed',
              opacity: activeId ? 1 : 0.45,
            }}
          >
            Resolve
          </button>
        </div>

        {/* Event log */}
        {log.length > 0 && (
          <div style={{ borderTop: '1px solid #374151', padding: '10px 14px', flexShrink: 0 }}>
            {label('Log')}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
              {log.map((entry, i) => (
                <div key={i} style={{ fontSize: '10px', color: '#9ca3af', fontFamily: 'monospace', lineHeight: 1.5, wordBreak: 'break-all' as const }}>
                  {entry}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Right panel: live preview ─────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div style={{ padding: '10px 20px', borderBottom: '1px solid #374151', background: '#111827', display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#f9fafb' }}>HIL Observe Panel — Live Preview</span>
          <span style={{ fontSize: '12px', color: '#9ca3af' }}>
            Mirrors the <strong style={{ color: '#d1d5db' }}>HIL Actions</strong> tab in the Flow Observer panel
          </span>
          {activeId && (
            <span style={{
              marginLeft: 'auto',
              padding: '2px 9px',
              borderRadius: '9999px',
              background: '#052e16',
              color: '#4ade80',
              fontSize: '11px',
              fontWeight: 700,
            }}>
              ACTIVE
            </span>
          )}
        </div>

        {/* Preview container */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '28px 24px', overflowY: 'auto', background: '#0f172a' }}>
          <div style={{
            width: '100%',
            maxWidth: '480px',
            minHeight: '360px',
            background: '#1f2937',
            border: '1px solid #374151',
            borderRadius: '8px',
            boxShadow: '0 2px 16px rgba(0,0,0,0.3)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <HilObservePanel />
          </div>
        </div>

        {/* Usage tip */}
        <div style={{ padding: '10px 20px', borderTop: '1px solid #374151', background: '#111827', flexShrink: 0 }}>
          <div style={{ fontSize: '11px', color: '#6b7280', lineHeight: 1.5 }}>
            <strong>Tip:</strong> Set <strong>Inbox Item ID</strong> to a real ID (from a running workflow's inbox) to test the full Approve/Reject API flow.
            Leave it at <strong>0</strong> to preview only the UI without hitting the backend.
            The timer preset uses a <strong>−10 s expiry</strong> to show the expired state immediately.
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Shared styles ────────────────────────────────────────────────────────────

const INPUT_STYLE: React.CSSProperties = {
  padding: '6px 8px',
  borderRadius: '5px',
  border: '1px solid #4b5563',
  fontSize: '13px',
  width: '100%',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  background: '#374151',
  color: '#e5e7eb',
};
